import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import BarraInferiorBotoes from '../../../../../components/BarraInferiorBotoes';
import BarraSubAbas from '../../../../../components/BarraSubAbas';
import ContainerConteudo from '../../../../../components/ContainerConteudo';
import { type CustomTableHeader } from '../../../../../components/CustomTable';
import DetailsTable from '../../../../../components/CustomTable/DetailsTable';
import CustomTableOffcanvas from '../../../../../components/CustomTableOffcanvas';
import WorkspaceBotoes from '../../../../../components/WorkspaceBotoes';
import { getUser } from '../../../../../modules/contexts/Auth/RequireAuth';
import { IFluxoAdminModeloRegistro } from '../../../../../modules/types/FluxoAdmin';
import { getAllFluxoAdminModelos } from '../../../../../services/fluxosAdmin/fluxoAdminModeloService';
import theme from '../../../../../theme';
import { useExcelExport } from '../../../../../utils/excel-export';
import {
  formataNomeMeioSiglas,
  formatDate,
  formatDateToFile,
} from '../../../../../utils/functions';
import { renderStatusDot } from '../../../ProdutosDAF/Produtos/auxiliarProdutosDaf';
import { optionStatusBin } from '../../Dominios/utils/optionStatusBin';
import { getSubAbas } from '../../subabas';

type FluxoModeloTableRow = {
  id: number;
  status: boolean;
  nomeModelo: string;
  setorNome: string;
  dominioNome: string;
  dominioTipoNome: string;
  etapas: React.ReactNode;
};

const headersTable: CustomTableHeader<FluxoModeloTableRow>[] = [
  { text: '', width: 1, renderable: false },
  { text: 'ID', key: 'id', width: 6, fieldType: 'number', textAlign: 'center' },
  {
    text: 'Status',
    key: 'status',
    width: 8,
    renderCell: (value) => renderStatusDot(value),
    fieldType: 'checkbox',
  },
  { text: 'Modelo', key: 'nomeModelo', width: 10 },
  { text: 'Setor', key: 'setorNome', width: 22 },
  { text: 'Domínio', key: 'dominioNome', width: 15 },
  { text: 'Tipo de Domínio', key: 'dominioTipoNome', width: 15 },
  { text: 'Etapas', key: 'etapas', width: 30 },
];

const valoresOffcanvas: Record<string, string> = {
  id: 'ID',
  dataRegistro: 'Registro',
  dataUltimaAtualizacao: 'Última Atualização',
  dataUltimaVisualizacao: 'Última Visualização',
  nEdicoes: 'Edições',
  nVisualizacoes: 'Visualizações',
  nVisualizadores: 'Visualizadores',

  separadorLog: '',
  separadorDados: '',

  status: 'Status',
  nomeModelo: 'Modelo',
  descricao: 'Descrição',
  setorNome: 'Setor',
  dominioNome: 'Domínio',
  linkMaisInfo: 'Link',
  observacao: 'Observações',
  etapas: 'Etapas',
};

const statusBadge = (status: number) => (
  <span
    style={{
      backgroundColor:
        status === 1 ? theme.colors.verdegov : theme.colors.vermelho,
      borderRadius: '10px',
      padding: '8px 10px',
      display: 'inline-block',
      minWidth: '70px',
      textAlign: 'center',
      color: 'white',
      fontWeight: 600,
    }}
  >
    {optionStatusBin.find((opt) => opt.value === status)?.label || 'N/I'}
  </span>
);

const FluxosAdminListagem: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const userID = user.sub;

  const [data, setData] = useState<IFluxoAdminModeloRegistro[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const fetchData = async () => {
    try {
      const registros = await getAllFluxoAdminModelos();
      setData(registros);
    } catch (error) {
      console.error('Erro ao buscar fluxos administrativos:', error);
      setData([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const tableData: FluxoModeloTableRow[] = useMemo(
    () =>
      data.map((item) => ({
        id: item.id,
        status: item.status === 1,
        nomeModelo: item.nomeModelo,
        setorNome: item.setorNome || '-',
        dominioNome: item.dominioNome || '-',
        dominioTipoNome: item.dominioTipoNome || '-',
        etapas: item.etapas || '-',
      })),
    [data],
  );

  const offCanvasData = useMemo(
    () =>
      data.map((item) => ({
        id: item.id,
        dataRegistro:
          formatDate(item.dataRegistro) +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioRegistro.nomeCompleto),
        dataUltimaAtualizacao:
          formatDate(item.dataUltimaAtualizacao) +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioAtualizacao.nomeCompleto),
        dataUltimaVisualizacao:
          formatDate(item.dataUltimaVisualizacao) +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioUltimaVisualizacao.nomeCompleto),
        nEdicoes: item.nEdicoes,
        nVisualizacoes: item.nVisualizacoes,
        nVisualizadores: item.nVisualizadores,

        separadorLog: '',
        status: statusBadge(item.status),

        separadorDados: '',
        nomeModelo: item.nomeModelo,
        descricao: item.descricao || 'Não informado',
        setorNome: item.setorNome || '-',
        dominioNome: item.dominioNome || '-',
        dominioTipoNome: item.dominioTipoNome || '-',
        linkMaisInfo: item.linkMaisInfo || '-',
        observacao: item.observacao || 'Não informado',
        etapas: item.etapas || '-',
      })),
    [data],
  );

  const offcanvasOpenRoute = useMemo(() => {
    if (typeof window === 'undefined' || !selectedId) return undefined;
    const fichaPath = `/modelo/${selectedId}/dadosgerais`;
    return `${window.location.origin}/#${location.pathname}${fichaPath}`;
  }, [location.pathname, selectedId]);

  const handleRowClick = (row: FluxoModeloTableRow) => {
    if (!row?.id) return;
    navigate(`modelo/${row.id}/dadosgerais`);
  };

  const handleOpenDetails = (row: FluxoModeloTableRow) => {
    if (!row?.id) return;
    setSelectedId(row.id);
  };

  const handleClickInserir = () => {
    navigate('modelo/dadosgerais');
  };

  const { exportData, isExporting } = useExcelExport(
    'listagemfluxosadminmodelos',
  );
  const handleExport = async () => {
    try {
      await exportData(
        data,
        `sisaf_fluxosadmin_${formatDateToFile(new Date().toISOString())}.xlsx`,
        userID,
        'Listagem de Fluxos Administrativos',
        'DAF',
      );
    } catch (error) {
      console.error('Erro na exportação:', error);
    }
  };

  return (
    <>
      <BarraSubAbas subAbas={getSubAbas()} />

      <ContainerConteudo>
        <CustomTableOffcanvas
          headers={headersTable}
          data={tableData}
          displayValues={valoresOffcanvas}
          offcanvasTitle="Fluxo Administrativo"
          widthOffcanvas="60%"
          openRoute={offcanvasOpenRoute}
          onOpenOffcanvas={handleOpenDetails}
          onRowClick={handleRowClick}
          itemsPerPage={50}
          showPagination
          showOpenButton
          filterable
          sortable
          minHeight="550px"
          data-testid="fluxos-admin-table"
        >
          {(row) => (
            <DetailsTable
              rows={offCanvasData}
              selectedId={row?.id ?? selectedId}
              displayValues={valoresOffcanvas}
            />
          )}
        </CustomTableOffcanvas>
      </ContainerConteudo>

      <BarraInferiorBotoes>
        <WorkspaceBotoes
          inserir
          onClickInserir={handleClickInserir}
          download
          downloadDisabled={isExporting}
          onClickDownload={handleExport}
        />
      </BarraInferiorBotoes>
    </>
  );
};

export default FluxosAdminListagem;
